import { useMutation, useQuery } from "@apollo/client";
import { useCallback } from "react";
import { getFragmentData, graphql } from "../gql";
import {
  OnboardingLiveFragment,
  OnboardingLiveSlotPlanFragment,
} from "./OnboardingLiveFragments.ts";
import { GetOnboardingLivesQuery } from "./useOnboardingLives.ts";

export const GetOnboardingLiveByIdQuery = graphql(`
  query getOnboardingLiveById($onboardingLiveId: String!) {
    getOnboardingLiveById(onboardingLiveId: $onboardingLiveId) {
      ...OnboardingLiveItem
    }
  }
`);

const CheckConductorChangeMutation = graphql(`
  mutation checkOnboardingLiveConductorChange(
    $onboardingLiveId: String!
    $teacherId: String
  ) {
    checkOnboardingLiveConductorChange(
      onboardingLiveId: $onboardingLiveId
      teacherId: $teacherId
    ) {
      ...OnboardingLiveSlotPlanItem
    }
  }
`);

const ChangeConductorMutation = graphql(`
  mutation changeOnboardingLiveConductor(
    $onboardingLiveId: String!
    $teacherId: String
    $makeReplacedConductorAvailableAgain: Boolean!
  ) {
    changeOnboardingLiveConductor(
      onboardingLiveId: $onboardingLiveId
      teacherId: $teacherId
      makeReplacedConductorAvailableAgain: $makeReplacedConductorAvailableAgain
    ) {
      plan {
        ...OnboardingLiveSlotPlanItem
      }
      onboardingLive {
        id
      }
    }
  }
`);

const DeleteOnboardingLiveMutation = graphql(`
  mutation deleteOnboardingLive(
    $onboardingLiveId: String!
    $makeConductorAvailableAgain: Boolean!
  ) {
    deleteOnboardingLive(
      onboardingLiveId: $onboardingLiveId
      makeConductorAvailableAgain: $makeConductorAvailableAgain
    )
  }
`);

const AddParticipantsMutation = graphql(`
  mutation addParticipantsToOnboardingLive(
    $onboardingLiveId: String!
    $userEmails: [String!]!
  ) {
    addParticipantsToOnboardingLive(
      onboardingLiveId: $onboardingLiveId
      userEmails: $userEmails
    ) {
      id
    }
  }
`);

const RemoveParticipantMutation = graphql(`
  mutation removeParticipantFromOnboardingLive(
    $onboardingLiveId: String!
    $userId: String!
  ) {
    removeParticipantFromOnboardingLive(
      onboardingLiveId: $onboardingLiveId
      userId: $userId
    ) {
      id
    }
  }
`);

export function useOnboardingLive(onboardingLiveId: string) {
  const { data, loading } = useQuery(GetOnboardingLiveByIdQuery, {
    variables: { onboardingLiveId },
    fetchPolicy: "cache-and-network",
  });
  const refetchQueries = [
    { query: GetOnboardingLiveByIdQuery, variables: { onboardingLiveId } },
    GetOnboardingLivesQuery,
  ];
  const [checkMutation, { loading: checking }] = useMutation(
    CheckConductorChangeMutation,
  );
  const [changeMutation, { loading: changing }] = useMutation(
    ChangeConductorMutation,
    { refetchQueries },
  );
  const [deleteMutation, { loading: deleting }] = useMutation(
    DeleteOnboardingLiveMutation,
    { refetchQueries: [GetOnboardingLivesQuery] },
  );
  const [addParticipantsMutation, { loading: addingParticipants }] =
    useMutation(AddParticipantsMutation, { refetchQueries });
  const [removeParticipantMutation, { loading: removingParticipant }] =
    useMutation(RemoveParticipantMutation, { refetchQueries });

  const checkConductorChange = useCallback(
    async (teacherId?: string) => {
      const { data } = await checkMutation({
        variables: { onboardingLiveId, teacherId },
      });
      return getFragmentData(
        OnboardingLiveSlotPlanFragment,
        data?.checkOnboardingLiveConductorChange,
      );
    },
    [checkMutation, onboardingLiveId],
  );
  const changeConductor = useCallback(
    async (input: {
      teacherId?: string;
      makeReplacedConductorAvailableAgain: boolean;
    }) => {
      const { data } = await changeMutation({
        variables: { onboardingLiveId, ...input },
      });
      return {
        plan: getFragmentData(
          OnboardingLiveSlotPlanFragment,
          data?.changeOnboardingLiveConductor.plan,
        ),
        changed: !!data?.changeOnboardingLiveConductor.onboardingLive,
      };
    },
    [changeMutation, onboardingLiveId],
  );
  const deleteOnboardingLive = useCallback(
    async (makeConductorAvailableAgain: boolean) => {
      await deleteMutation({
        variables: { onboardingLiveId, makeConductorAvailableAgain },
      });
    },
    [deleteMutation, onboardingLiveId],
  );
  const addParticipants = useCallback(
    async (userEmails: string[]) => {
      await addParticipantsMutation({
        variables: { onboardingLiveId, userEmails },
      });
    },
    [addParticipantsMutation, onboardingLiveId],
  );
  const removeParticipant = useCallback(
    async (userId: string) => {
      await removeParticipantMutation({
        variables: { onboardingLiveId, userId },
      });
    },
    [removeParticipantMutation, onboardingLiveId],
  );

  return {
    onboardingLive: getFragmentData(
      OnboardingLiveFragment,
      data?.getOnboardingLiveById,
    ),
    loading,
    checkConductorChange,
    changeConductor,
    deleteOnboardingLive,
    addParticipants,
    removeParticipant,
    checking,
    changing,
    deleting,
    updatingParticipants: addingParticipants || removingParticipant,
  };
}
